import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { isMobileApp, openWebappWithAuth } from '../utils/platform';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LowCreditModal = ({ user, onClose, forceShow = false, onRechargeSuccess, context = 'general', excludePaths = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  
  // Different messages based on context
  const messages = {
    general: {
      title: "Low Credit Balance",
      subtitle: "Recharge now to continue using Nerd AI features!",
      highlight: "You're running low on credits"
    },
    download: {
      title: "You Need 30 Credits to Download",
      subtitle: "Recharge now to download your personalized career roadmap!",
      highlight: "30 credits required to download Career Roadmap"
    }
  };
  
  const currentMessage = messages[context] || messages.general;

  useEffect(() => {
    // If forceShow is true (manual trigger), always show
    if (forceShow) {
      setIsOpen(true);
      return;
    }
    
    // Check if current path is in excluded paths
    const isExcludedPath = excludePaths.some(path => location.pathname === path);
    if (isExcludedPath) {
      setIsOpen(false);
      return;
    }
    
    // Otherwise, check if user is logged in, not admin, has low credits, and hasn't dismissed
    const isAdmin = user?.email === 'shifracs@gmail.com';
    const credits = user?.nerd_ai_credits || 0;
    const hasLowCredits = credits < 17;
    
    if (user && !isAdmin && hasLowCredits && !isDismissed) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [user, isDismissed, forceShow, location.pathname, excludePaths]);

  const handleClose = () => {
    setIsOpen(false);
    setIsDismissed(true);
    if (onClose) onClose(); // Call parent's onClose if provided
  };

  const handleBuyCredits = async () => {
    // For mobile app, open webapp homepage with auto-login
    if (isMobileApp()) {
      await openWebappWithAuth('/'); // Open homepage with auto-login
      handleClose();
      return;
    }
    
    // For web: Directly trigger 100 credit purchase (₹100) without leaving the page
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Please login to purchase credits');
        setLoading(false);
        return;
      }
      
      // Create order for 100 credits (small package)
      const orderResponse = await fetch(`${BACKEND_URL}/api/credits/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          package: 'small' // 100 credits package
        })
      });

      const orderData = await orderResponse.json();

      if (!orderData.success) {
        throw new Error(orderData.detail || 'Failed to create order');
      }
      
      // Check if Razorpay is loaded
      if (!window.Razorpay) {
        throw new Error('Razorpay library not loaded. Please refresh the page.');
      }

      // Razorpay payment
      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.order_id,
        name: 'Nerd AI',
        description: `${orderData.credits} Nerd-AI Credits`,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await fetch(`${BACKEND_URL}/api/credits/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              })
            });

            const verifyData = await verifyResponse.json();

            if (verifyData.success) {
              toast.success(`Successfully purchased ${orderData.credits} credits!`);
              window.dispatchEvent(new Event('creditUpdated')); // Trigger live update
              setIsOpen(false);
              
              // Call success callback to trigger download
              if (onRechargeSuccess) {
                onRechargeSuccess();
              }
              
              if (onClose) onClose();
            } else {
              toast.error('Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Failed to verify payment');
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        },
        prefill: {
          email: user?.email || '',
          name: user?.name || ''
        },
        theme: {
          color: '#f97316'
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.message || 'Failed to initiate purchase');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Modal */}
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative animate-fadeIn"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Content */}
          <div className="p-6 sm:p-8">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="8" fill="currentColor" opacity="0.3"/>
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-3">
              {currentMessage.title}
            </h2>

            {/* LIVE Current Balance - Always Show */}
            <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-300 rounded-lg p-4 mb-4">
              <div className="text-center">
                <p className="text-sm text-gray-700 font-semibold mb-2">Your Current Balance</p>
                <p className="text-4xl font-bold text-orange-600">{user?.nerd_ai_credits || 0}</p>
                <p className="text-xs text-gray-600 mt-1">credits</p>
              </div>
            </div>

            {/* Required Credits / Low Balance Highlight */}
            <div className="bg-blue-50 border border-blue-300 rounded-lg p-3 mb-4">
              <p className="text-center text-sm text-blue-900">
                <span className="font-bold text-lg">{currentMessage.highlight}</span>
              </p>
            </div>

            {/* Description */}
            <p className="text-gray-600 text-center mb-6 text-sm sm:text-base font-medium">
              {currentMessage.subtitle}
            </p>

            {/* Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handleBuyCredits}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : (isMobileApp() ? 'Get More AI Credits' : 'Recharge ₹100 for 100 Credits')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default LowCreditModal;
